import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { BRAND_LOGOS } from '@app/constants/credit-card-logos';
import { Company } from '@app/models/company/company.model';
import { SelfServeOnboarding } from '@app/models/self-serve/self-serve-onboarding.model';
import { Card } from '@app/models/settings/billing/card.model';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { AnalyticEvents } from '@app/enums';
import { OnboardingStatusService } from '@self-serve/services/onboarding-status.service';
import { Router } from '@angular/router';

@Component({
    templateUrl: './review.template.html',
    styleUrls: ['../../../self-serve.style.scss', 'review.style.scss'],
})
export class ReviewView implements OnInit {
    @ViewChild('form', { static: true }) form!: NgForm;
    selfServeOnboarding: SelfServeOnboarding = new SelfServeOnboarding();
    isLoading = true;
    card: Card | null = null;
    readonly BRAND_LOGOS = BRAND_LOGOS;
    private _meetingDate: Date | null = null;

    constructor(
        private analyticService: AnalyticService,
        private authService: AuthService,
        private onboardingStatusService: OnboardingStatusService,
        private notifyService: NotifyService,
        private router: Router
    ) {}

    async ngOnInit(): Promise<void> {
        try {
            // Skip loading unnecessary data if the user has already booked their call.
            // This makes the loading animation faster.
            this.meetingDate = this.authService.company.selfServeQuickstart.cxMeetingAt;
            if (this.meetingDate) {
                this.isLoading = false;
                return;
            }

            const [company, [cards]] = await Promise.all([
                Company.with(['selfServeOnboarding', 'selfServeQuickstart']).find(this.authService.company.id),
                Card.all(),
            ]);

            this.meetingDate = company.selfServeQuickstart.cxMeetingAt;

            if (cards.length) {
                // companies can only have one card
                this.card = cards[0];
            }

            this.selfServeOnboarding = company.selfServeOnboarding;

            this.isLoading = false;
        } catch (error) {
            this.notifyService.error(error);
        }
    }

    get meetingDate(): Date | null {
        return this._meetingDate;
    }

    set meetingDate(value: Date | null) {
        this._meetingDate = value;
        this.onboardingStatusService.refresh();
    }

    get rescheduleDate(): Date | null {
        if (!this._meetingDate) {
            return null;
        }

        const rescheduleDate = new Date(this._meetingDate);
        rescheduleDate.setHours(rescheduleDate.getHours() - 24); // subtract 24 hours

        return rescheduleDate;
    }

    trackSelfServeChangePayrollTimeline(): void {
        this.analyticService.trackEvent(AnalyticEvents.SelfServeBookYourCallClickChangeTimeline);
        this.router.navigate(['/company-onboarding', 'payroll-situation', 'tell-us']);
    }

    trackSelfServeChangePaymentInformation(): void {
        this.analyticService.trackEvent(AnalyticEvents.SelfServeBookYourCallClickChangePayment);
        this.router.navigate(['/settings', 'billing']);
    }

    handleClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        if (target.tagName === 'A') {
            this.analyticService.trackEvent(AnalyticEvents.SelfServeBookYourCallClickContactUs);
        }
    }
}
